import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { DonkeyMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

const MESSAGES = [
  "Separating experience from wishful thinking…",
  "Looking for customers you could actually reach…",
  "Removing ideas that only work in motivational videos…",
  "Checking what might stop this from working…",
  "Weighing credibility against ambition…",
  "Preparing the Donkey's Verdict…",
];

export default function Analysis() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [msgIndex, setMsgIndex] = useState(0);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const rotate = setInterval(() => setMsgIndex((i) => (i + 1) % MESSAGES.length), 2600);
    const poll = async () => {
      try {
        const { data } = await api.get(`/reports/${id}/status`);
        if (data.gen_status === "complete") {
          clearInterval(pollRef.current);
          navigate(`/report/${id}`, { replace: true });
        } else if (data.gen_status === "failed") {
          clearInterval(pollRef.current);
          setFailed(true);
        }
      } catch { /* keep polling */ }
    };
    poll();
    pollRef.current = setInterval(poll, 2500);
    return () => { clearInterval(rotate); clearInterval(pollRef.current); };
  }, [id, navigate]);

  const retry = async () => {
    setRetrying(true);
    setFailed(false);
    try {
      await api.post(`/reports/${id}/retry`);
      setMsgIndex(0);
      pollRef.current = setInterval(async () => {
        const { data } = await api.get(`/reports/${id}/status`);
        if (data.gen_status === "complete") { clearInterval(pollRef.current); navigate(`/report/${id}`, { replace: true }); }
        else if (data.gen_status === "failed") { clearInterval(pollRef.current); setFailed(true); setRetrying(false); }
      }, 2500);
    } catch { setFailed(true); setRetrying(false); }
  };

  if (failed) {
    return (
      <div className="min-h-screen bg-donkey-bg grain flex flex-col items-center justify-center px-5 text-center">
        <AlertTriangle className="w-14 h-14 text-donkey-amber mb-6" />
        <h1 className="font-heading font-bold text-2xl text-donkey-warm">The donkey tripped over a rock.</h1>
        <p className="mt-3 text-donkey-grey max-w-md">
          Generation didn't complete. Your interview is safe — nothing was charged. Give it another go.
        </p>
        <Button onClick={retry} disabled={retrying}
          className="mt-8 bg-donkey-orange text-donkey-black font-semibold px-7 h-12" data-testid="retry-analysis-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${retrying ? "animate-spin" : ""}`} /> Try again
        </Button>
        <button onClick={() => navigate("/dashboard")} className="mt-4 text-sm text-donkey-grey hover:text-donkey-warm">
          Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-donkey-bg grain flex flex-col items-center justify-center px-5 text-center">
      <div className="relative mb-10">
        <div className="absolute inset-0 rounded-full bg-donkey-orange/20 blur-2xl animate-pulse-soft" />
        <DonkeyMark size={80} className="relative animate-pulse-soft" />
      </div>
      <h1 className="font-heading font-bold text-2xl sm:text-3xl text-donkey-warm">The donkey is thinking.</h1>
      <p className="mt-3 text-donkey-grey">This takes slightly longer than choosing 'dropshipping' at random.</p>
      <p key={msgIndex} className="mt-10 text-donkey-orange animate-fade-up text-sm" data-testid="analysis-status">
        {MESSAGES[msgIndex]}
      </p>
      <div className="mt-6 flex gap-1.5">
        {MESSAGES.map((_, i) => (
          <span key={i} className={`h-1.5 w-8 rounded-full transition-colors ${i <= msgIndex ? "bg-donkey-orange" : "bg-white/10"}`} />
        ))}
      </div>

      <div className="mt-10 max-w-md w-full rounded-2xl border border-donkey-line bg-white/[0.02] p-5" data-testid="analysis-reassurance">
        <p className="text-sm text-donkey-warm/90">
          {elapsed < 300
            ? "This usually takes 2–4 minutes — the Donkey is doing real market research, not guessing."
            : "Still working — some verdicts take a little longer. Nothing's broken, and nothing was charged twice."}
        </p>
        <p className="mt-2 text-xs text-donkey-grey" data-testid="analysis-elapsed">{fmtElapsed(elapsed)} elapsed</p>
        <p className="mt-3 text-sm text-donkey-grey leading-relaxed">
          You can safely leave this page and come back later — we'll keep working and your Verdict will be waiting on your dashboard.
        </p>
        <Button variant="outline" onClick={() => navigate("/dashboard")}
          className="mt-4 border-donkey-line bg-transparent text-donkey-warm hover:bg-white/5"
          data-testid="analysis-leave-btn">
          Leave and come back later
        </Button>
      </div>
    </div>
  );
}

function fmtElapsed(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}
